import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const Login = () => {
  const { login, loading, isAuthed } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isAuthed) navigate("/");
  }, [isAuthed, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const res = await login(email, password);
    if (res.ok) navigate("/");
    else setErr(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 overflow-hidden">
      {/* LEFT: Karthikk Keertti — profile card */}
      <div className="hidden lg:flex relative overflow-hidden" style={{ background: "#0E1626" }}>
        {/* Soft gradient background */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #0E1626 0%, #1A2332 55%, #0E1626 100%)",
          }}
        />
        {/* Ambient glows */}
        <motion.div
          aria-hidden
          className="absolute"
          animate={{
            opacity: [0.3, 0.5, 0.3],
            x: ["-5%", "5%", "-5%"],
            y: ["0%", "-3%", "0%"],
          }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            top: "10%",
            left: "15%",
            width: "55%",
            height: "55%",
            background:
              "radial-gradient(circle, rgba(245,158,11,0.25) 0%, rgba(245,158,11,0) 60%)",
            filter: "blur(40px)",
          }}
        />
        <motion.div
          aria-hidden
          className="absolute"
          animate={{
            opacity: [0.2, 0.4, 0.2],
            x: ["5%", "-5%", "5%"],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          style={{
            bottom: "5%",
            right: "10%",
            width: "55%",
            height: "55%",
            background:
              "radial-gradient(circle, rgba(64,141,251,0.25) 0%, rgba(64,141,251,0) 60%)",
            filter: "blur(50px)",
          }}
        />
        {/* Decorative accent line */}
        <motion.div
          aria-hidden
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
          className="absolute top-10 left-10 h-px w-20 origin-left"
          style={{ background: "linear-gradient(90deg, rgba(245,158,11,0.8), transparent)" }}
        />

        {/* Profile content */}
        <div className="relative z-10 w-full h-full overflow-y-auto">
          <div className="p-10 md:p-12 text-white">
            {/* Header: avatar + name + headline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex items-start gap-5"
            >
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold shrink-0 shadow-xl"
                style={{
                  background:
                    "linear-gradient(135deg, #F59E0B 0%, #D97706 60%, #92400E 100%)",
                }}
                data-testid="profile-avatar"
              >
                KK
              </div>
              <div className="min-w-0 pt-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/90 mb-2">
                  The founder
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight leading-tight" data-testid="profile-name">
                  Karthikk Keertti
                </h1>
                <div className="text-sm text-white/85 mt-2 leading-snug">
                  CEO &amp; Founder — <span className="text-amber-200">DigiTrans</span>
                  <br />
                  Founder — <span className="text-amber-200">VakuAI</span>
                </div>
                <div className="text-xs text-white/55 mt-2 flex items-center gap-2">
                  <span>Dubai, United Arab Emirates</span>
                  <span className="w-1 h-1 rounded-full bg-white/40" />
                  <span>66 team · UAE + India</span>
                </div>
              </div>
            </motion.div>

            {/* About */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8"
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300/80 mb-2">
                About
              </div>
              <p className="text-sm text-white/85 leading-relaxed max-w-lg">
                Building <strong className="text-white">DigiTrans</strong> — a Dubai-based
                digital, technology, data &amp; AI advisory, and an official partner of
                Oracle Fusion Cloud. Also behind <strong className="text-white">VakuAI</strong>, an
                enterprise AI Super‑Agent platform out of New York.
              </p>
            </motion.div>

            {/* Experience */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-7"
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300/80 mb-3">
                Experience
              </div>
              <ul className="space-y-3 text-sm">
                {[
                  { role: "CEO & Founder", org: "DigiTrans", years: "Sep 2022 – Present", current: true },
                  { role: "Founder", org: "VakuAI (New York)", years: "Dec 2025 – Present", current: true },
                  { role: "Founder", org: "SeerTree Solutions", years: "Nov 2013 – Sep 2022" },
                  { role: "Project Manager", org: "TransSys Solutions", years: "2011 – 2013" },
                  { role: "Solutions Architect", org: "HCLTech", years: "2009 – 2011" },
                  { role: "Application Consultant", org: "Tech Mahindra", years: "2007 – 2009" },
                  {
                    role: "Business Process Support & Team Grooming",
                    org: "KPO · Bookkeeping & accounting for AT&T, Yahoo, QuickBooks, Quicken (India)",
                    years: "2004 – 2007",
                  },
                  { role: "Store Manager", org: "McDonald's", years: "2001 – 2004" },
                ].map((e, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`profile-exp-${i}`}>
                    <span
                      className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                        e.current ? "bg-amber-400" : "bg-white/30"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-medium leading-tight">
                        {e.role}
                        <span className="text-white/50 font-normal"> · {e.org}</span>
                      </div>
                      <div className="text-[11px] text-white/50 mt-0.5">{e.years}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Education */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-7 pb-4"
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300/80 mb-3">
                Education
              </div>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/30 shrink-0" />
                  <div>
                    <div className="text-white font-medium leading-tight">
                      MBA — Operations &amp; Finance
                    </div>
                    <div className="text-[11px] text-white/50 mt-0.5">Amity University</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/30 shrink-0" />
                  <div>
                    <div className="text-white font-medium leading-tight">
                      CWA — Cost Accounting
                    </div>
                    <div className="text-[11px] text-white/50 mt-0.5">
                      Institute of Cost Accountants of India
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/30 shrink-0" />
                  <div>
                    <div className="text-white font-medium leading-tight">
                      B.Com (Honours)
                    </div>
                    <div className="text-[11px] text-white/50 mt-0.5">
                      University of Delhi
                    </div>
                  </div>
                </li>
              </ul>
            </motion.div>

            {/* Countries travelled */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-7"
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300/80 mb-3 flex items-center gap-2">
                <span>Countries travelled</span>
                <span className="text-white/40 normal-case tracking-normal font-sans">19</span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="profile-countries">
                {[
                  { flag: "🇦🇪", name: "UAE" },
                  { flag: "🇰🇼", name: "Kuwait" },
                  { flag: "🇯🇴", name: "Jordan" },
                  { flag: "🇧🇭", name: "Bahrain" },
                  { flag: "🇶🇦", name: "Qatar" },
                  { flag: "🇪🇬", name: "Egypt" },
                  { flag: "🇸🇩", name: "Sudan" },
                  { flag: "🇸🇸", name: "South Sudan" },
                  { flag: "🇸🇦", name: "Saudi Arabia" },
                  { flag: "🇮🇶", name: "Iraq" },
                  { flag: "🇺🇸", name: "United States" },
                  { flag: "🇰🇪", name: "Kenya" },
                  { flag: "🇳🇬", name: "Nigeria" },
                  { flag: "🇿🇲", name: "Zambia" },
                  { flag: "🇸🇬", name: "Singapore" },
                  { flag: "🇯🇵", name: "Japan" },
                  { flag: "🇹🇭", name: "Thailand" },
                  { flag: "🇮🇩", name: "Indonesia" },
                  { flag: "🇱🇰", name: "Sri Lanka" },
                ].map((c) => (
                  <span
                    key={c.name}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-[11px] text-white/85 hover:bg-white/10 hover:border-amber-300/40 transition-colors"
                  >
                    <span className="text-sm leading-none">{c.flag}</span>
                    {c.name}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Footer tagline */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.95 }}
              className="mt-8 pt-5 border-t border-white/10 flex items-center gap-3"
            >
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.3 }}
                    className="w-1 h-1 rounded-full bg-amber-300"
                  />
                ))}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/50">
                karthikkkeertti.com
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* RIGHT: login form */}
      <div className="relative flex items-center justify-center px-6 py-12 bg-[#FAFAFA] overflow-hidden">
        {/* Subtle ambient gradient */}
        <div
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(64,141,251,0.08) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(245,158,11,0.08) 0%, transparent 50%)",
          }}
        />

        <div className="relative w-full max-w-md">
          {/* Logo lockup with floating animation */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex items-center gap-4 mb-8"
            data-testid="login-logo-lockup"
          >
            <motion.div
              whileHover={{ scale: 1.05, rotate: -2 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="bg-white border border-gray-200 p-1.5 flex items-center justify-center shadow-sm"
              style={{ width: 68, height: 68, borderRadius: 8 }}
            >
              <img
                src="/logos/vaku.png"
                alt="Vaku"
                className="w-full h-full object-contain"
                data-testid="login-vaku-logo"
              />
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05, rotate: 2 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="bg-white border border-gray-200 px-3 py-2 flex items-center justify-center shadow-sm"
              style={{ height: 68, borderRadius: 8 }}
            >
              <img
                src="/logos/digitrans.png"
                alt="DigiTrans"
                className="h-full object-contain"
                data-testid="login-digitrans-logo"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="mb-10"
          >
            <h2
              className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A] leading-tight"
              data-testid="login-heading"
            >
              Manage{" "}
              <span className="bg-gradient-to-r from-[#1A2332] via-[#408DFB] to-[#B8860B] bg-clip-text text-transparent">
                DigiTrans global
              </span>{" "}
              services and{" "}
              <span className="bg-gradient-to-r from-[#B8860B] to-[#1A2332] bg-clip-text text-transparent">
                Vaku AI product
              </span>
            </h2>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            onSubmit={handleSubmit}
            className="space-y-5"
            data-testid="login-form"
          >
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-2 block">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@company.com"
                className="w-full border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-mono focus:border-[#1A2332] focus:ring-2 focus:ring-[#408DFB]/20 outline-none transition-all rounded-md"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-2 block">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  className="w-full border border-[#E5E5E5] bg-white px-4 py-3 pr-12 text-sm font-mono focus:border-[#1A2332] focus:ring-2 focus:ring-[#408DFB]/20 outline-none transition-all rounded-md"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  data-testid="toggle-password-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#0A0A0A]"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {err && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                data-testid="login-error"
                className="bg-[#FEF2F2] border border-[#E53E3E] text-[#991B1B] text-sm px-4 py-3 font-mono rounded-md"
              >
                {err}
              </motion.div>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full bg-[#1A2332] text-white px-4 py-3.5 font-medium tracking-wide text-sm hover:bg-[#243042] transition-colors rounded-md flex items-center justify-center gap-2 disabled:opacity-60 shadow-md"
            >
              {loading ? "Signing in..." : "Sign In"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </motion.button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-12 pt-6 border-t border-[#E5E5E5]"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#999]">
              Secured by JWT · Zero session leakage
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
